// Pure derivation helpers over the cybersecurity scoring reference payload
// (see GET /api/v1/cybersecurity-scoring-reference/). No hardcoded scores here —
// everything is read from `reference` so a rule change in Python reaches the UI
// without a second edit.

export const IMPACT_STOP = {Negligible: 1, Moderate: 2, Major: 3, Severe: 4};
export const FEASIBILITY_STOP = {'Very Low': 1, Low: 2, Medium: 3, High: 4};
export const RISK_LEVEL_STOP = {1: 0, 2: 1, 3: 2, 4: 3, 5: 4};

const IMPACT_KEYS = ['safety', 'financial', 'operational', 'privacy'];
const FEASIBILITY_KEYS = [
  'required_time',
  'expertise',
  'knowledge_of_product',
  'opportunity_for_attack',
  'equipment',
];

// impact levels are stored and keyed by code; the label is what users see
export function impactLabel(reference, level) {
  return reference?.impact_level_labels?.[level] ?? level;
}

export function factorScore(reference, factorKey, optionCode) {
  const factor = reference?.feasibility_factors?.find(
    item => item.key === factorKey,
  );
  if (!factor || !optionCode) {
    return null;
  }
  const option = factor.options?.find(item => item.code === optionCode);
  return option ? option.score : null;
}

export function mostSevereImpact(reference, impacts) {
  if (!reference?.impact_levels) {
    return '';
  }
  const present = new Set(
    IMPACT_KEYS.map(key => impacts?.[key]).filter(Boolean),
  );
  for (const level of reference.impact_levels) {
    if (present.has(level)) {
      return level;
    }
  }
  return '';
}

// running sum of whichever factors are set; null until at least one is
export function feasibilityTotal(reference, factors) {
  if (!reference?.feasibility_factors) {
    return null;
  }
  let total = null;
  for (const key of FEASIBILITY_KEYS) {
    const score = factorScore(reference, key, factors?.[key]);
    if (score !== null) {
      total = (total ?? 0) + score;
    }
  }
  return total;
}

export function feasibilityComplete(reference, factors) {
  if (!reference?.feasibility_factors) {
    return false;
  }
  return FEASIBILITY_KEYS.every(key => factorScore(reference, key, factors?.[key]) !== null);
}

export function feasibilityRating(reference, total) {
  if (!reference?.feasibility_thresholds || total === null || total === undefined) {
    return '';
  }
  for (const {max, rating} of reference.feasibility_thresholds) {
    if (max === null || total <= max) {
      return rating;
    }
  }
  return '';
}

export function isComplete(reference, impacts, factors) {
  const impactsSet = IMPACT_KEYS.every(key => Boolean(impacts?.[key]));
  return impactsSet && feasibilityComplete(reference, factors);
}

export function deriveEvaluation(reference, impacts, factors) {
  if (!reference) {
    return {
      complete: false,
      most_severe_impact: null,
      most_severe_impact_label: null,
      feasibility_total: null,
      feasibility_rating: null,
      risk_value: null,
      risk_level: null,
      response_policy: null,
      deliberation: null,
    };
  }

  const impact = mostSevereImpact(reference, impacts);
  const total = feasibilityTotal(reference, factors);
  const factorsSet = feasibilityComplete(reference, factors);
  const rating = factorsSet ? feasibilityRating(reference, total) : '';
  const complete = isComplete(reference, impacts, factors);
  const riskValue = complete ? reference.risk_matrix?.[impact]?.[rating] ?? null : null;
  const riskLevel = riskValue !== null ? reference.risk_levels?.[String(riskValue)] ?? null : null;

  return {
    complete,
    most_severe_impact: impact,
    most_severe_impact_label: impactLabel(reference, impact),
    feasibility_total: total,
    feasibility_rating: rating,
    risk_value: riskValue,
    risk_level: riskLevel,
    response_policy:
      riskValue !== null ? reference.response_policies?.[String(riskValue)] ?? null : null,
    deliberation:
      riskValue !== null ? reference.deliberations?.[String(riskValue)] ?? null : null,
  };
}
